import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { memoize } from 'lodash';
import * as React from 'react';

import { Bem } from '../../../service/Bem/Bem';
import { TestBem } from '../../../service/TestBem';
import { PhrasesSelectorMinusPhrasesActions, PhrasesSelectorWizardId } from '../../../typings/phrases';
import { ScreenErrorMessage } from '../../ScreenErrorMessage/ScreenErrorMessage';
import { SelectionList } from '../../SelectionList/SelectionList';
import { PhrasesSelectorMinusPhrasesAddForm } from '../PhrasesSelectorMinusPhrasesAddForm/PhrasesSelectorMinusPhrasesAddForm';
import { PhrasesSelectorMinusPhrasesCell } from '../PhrasesSelectorMinusPhrasesCell/PhrasesSelectorMinusPhrasesCell';
import {
    PhrasesSelectorSelectedBlock,
    PhrasesSelectorSelectedBlockActions
} from '../PhrasesSelectorSelectedBlock/PhrasesSelectorSelectedBlock';

import './PhrasesSelectorMinusPhrases.css';
import * as keyset from './PhrasesSelectorMinusPhrases.i18n';

const i18n = i18nFactory(keyset);

export interface PhrasesSelectorMinusPhrasesProps {
    wizardId: PhrasesSelectorWizardId;
    minusPhrases: Array<string>;
    onRemove: (index: number) => void;
    onChange: (index: number, phrase: string) => void;
    onAdd: (phrase: string) => void;
    onMinusPhrasesClear: () => void;
}

const BLOCK = 'phrases-selector-minus-phrases';

export class PhrasesSelectorMinusPhrases extends React.PureComponent<PhrasesSelectorMinusPhrasesProps> {
    private minusPhrasesColumns = memoize((minusPhrases, onChange, onRemove) => [
        {
            getCellContent: (rowIndex: number) => {
                const item = minusPhrases[rowIndex];

                return (
                    <PhrasesSelectorMinusPhrasesCell
                        index={rowIndex}
                        minusPhrase={item}
                        onRemove={onRemove}
                        onChange={onChange}
                    />
                );
            },
            flexGrow: 1
        }
    ]);

    private getActions = memoize(
        (areMinusPhrasesEmpty: boolean): Record<string, PhrasesSelectorSelectedBlockActions> => ({
            [PhrasesSelectorMinusPhrasesActions.remove]: {
                info: {
                    text: i18n('remove-action'),
                    val: PhrasesSelectorMinusPhrasesActions.remove,
                    elemMods: {
                        disabled: areMinusPhrasesEmpty
                    }
                },
                handler: this.props.onMinusPhrasesClear
            }
        })
    );

    render() {
        const { minusPhrases, onChange, onRemove } = this.props;
        const minusPhrasesCount = minusPhrases.length;

        return (
            <PhrasesSelectorSelectedBlock
                className={classNames(Bem.cls(BLOCK), TestBem.cls(BLOCK))}
                title={i18n('title')}
                actions={this.getActions(minusPhrasesCount === 0)}
            >
                <PhrasesSelectorMinusPhrasesAddForm onAdd={this.onAdd} />
                {minusPhrasesCount === 0 ? (
                    <div className={Bem.cls(BLOCK, 'empty-message-wrapper')}>
                        <ScreenErrorMessage
                            cls={Bem.cls(BLOCK, 'empty-message')}
                            header={i18n('empty-header')}
                            text={i18n('empty-text')}
                        />
                    </div>
                ) : (
                    <SelectionList
                        cls={Bem.cls(BLOCK, 'list')}
                        columns={this.minusPhrasesColumns(minusPhrases, onChange, onRemove)}
                        rowsCount={minusPhrasesCount}
                        rowClassNameGetter={this.rowClassNameGetter}
                        rowHeight={38}
                    />
                )}
            </PhrasesSelectorSelectedBlock>
        );
    }

    private rowClassNameGetter = () => Bem.cls(BLOCK, 'selection-row');

    private onAdd = (phrase: string) => {
        const { onAdd } = this.props;

        onAdd(phrase);
    };
}
